import { useMemo, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { api } from '../../lib/api.js';
import { useApi } from '../../hooks/useApi.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import { Button } from '../../components/Button.jsx';
import { NewInkModal } from './NewInkModal.jsx';

const Swatch = ({ hex }) => (
  <span
    class={`block h-8 w-8 shrink-0 rounded-md border ${hex ? 'border-border-subtle' : 'border-dashed border-border-strong'}`}
    style={hex ? { background: hex } : undefined}
    aria-hidden="true"
  />
);

const InkRow = ({ ink, bookId }) => {
  const measured = ink.targetLab != null;
  return (
    <a
      href={`/ink-books/${bookId}/inks/${ink.id}`}
      class="flex items-center gap-3 border-b border-border-subtle px-4 py-3 transition hover:bg-bg-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
    >
      <Swatch hex={ink.targetHex} />
      <div class="min-w-0 flex-1">
        <p class="m-0 truncate font-serif text-[15px] font-medium text-text-primary">
          {ink.name}
        </p>
        <p class="m-0 text-[12px] font-medium text-text-tertiary">
          {measured
            ? `L ${ink.targetLab.l.toFixed(1)}  a ${ink.targetLab.a.toFixed(1)}  b ${ink.targetLab.b.toFixed(1)}`
            : 'Not yet measured'}
          {ink.pantoneCode && <span class="ml-3 text-text-muted">· {ink.pantoneCode}</span>}
        </p>
      </div>
    </a>
  );
};

const RegistryRow = ({ name, onClaim }) => (
  <button
    type="button"
    onClick={() => onClaim(name)}
    class="flex w-full items-center gap-3 border-b border-border-subtle px-4 py-2.5 text-left transition hover:bg-bg-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
  >
    <Swatch hex={null} />
    <p class="m-0 flex-1 font-serif text-[14px] font-medium text-text-secondary">{name}</p>
    <span class="text-[11px] font-semibold uppercase tracking-eyebrow text-text-muted">
      Claim ↗
    </span>
  </button>
);

export const BookDetailPage = ({ bookId }) => {
  const { route } = useLocation();
  const { data: book, loading, error, refetch } = useApi(`/ink-books/${bookId}`);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [search, setSearch] = useState('');
  const [newOpen, setNewOpen] = useState(false);
  const [prefilledName, setPrefilledName] = useState('');
  const [busy, setBusy] = useState(false);

  const inks = book?.inks ?? [];
  const registryNames = book?.registryNames ?? [];

  const filteredInks = useMemo(() => {
    if (!search) return inks;
    const q = search.toLowerCase();
    return inks.filter((i) => i.name.toLowerCase().includes(q));
  }, [inks, search]);

  // Show all registry names by default (capped). Once searching, filter to query
  // and exclude registry items already saved.
  const filteredRegistry = useMemo(() => {
    if (registryNames.length === 0) return [];
    const existing = new Set(inks.map((i) => i.name.toLowerCase()));
    const candidates = registryNames.filter((n) => !existing.has(n.toLowerCase()));
    if (!search) return candidates.slice(0, 100);
    const q = search.toLowerCase();
    return candidates.filter((n) => n.toLowerCase().includes(q)).slice(0, 200);
  }, [registryNames, inks, search]);

  const handleClaim = (name) => {
    setPrefilledName(name);
    setNewOpen(true);
  };

  const handleAddInk = () => {
    setPrefilledName('');
    setNewOpen(true);
  };

  const handleDeleteBook = async () => {
    if (!window.confirm(`Delete "${book.name}"? Inks and mixes inside will also be deleted.`)) return;
    setBusy(true);
    try {
      await api.delete(`/ink-books/${book.id}`);
      route('/ink-books');
    } catch (err) {
      alert(err.message ?? 'Failed to delete');
      setBusy(false);
    }
  };

  return (
    <div>
      <a href="/ink-books" class="mb-3 inline-block text-[12px] font-semibold uppercase tracking-eyebrow text-text-tertiary hover:text-text-primary">
        ← Ink Books
      </a>

      {loading && <p class="text-sm text-text-tertiary">Loading…</p>}
      {error && (
        <p class="text-sm font-semibold text-ink-magenta">
          Failed to load: {error.message}
        </p>
      )}

      {book && (
        <>
          <section class="mb-6 flex items-start justify-between gap-4">
            <div>
              <p class="eyebrow mb-1.5" style={{ color: '#00B4D8' }}>
                {book.kind === 'pantone' ? 'Pantone book' : book.kind === 'designer' ? 'Designer book' : 'Custom book'}
              </p>
              <h1 class="m-0 mb-1.5 text-[30px] leading-[1.18] tracking-tight text-text-primary">
                {book.name}
              </h1>
              <p class="m-0 text-sm text-text-secondary">
                {book.inks.length} {book.inks.length === 1 ? 'ink saved' : 'inks saved'}
                {registryNames.length > 0 && (
                  <span class="text-text-muted">
                    {' · '}{registryNames.length.toLocaleString()} names in the registry
                  </span>
                )}
              </p>
            </div>
            <div class="flex shrink-0 gap-2">
              {isAdmin && (
                <>
                  <Button variant="secondary" onClick={handleAddInk}>+ Add ink</Button>
                  {!book.isSystem && (
                    <Button variant="ghost" onClick={handleDeleteBook} disabled={busy} class="text-ink-magenta hover:text-ink-magenta">
                      Delete book
                    </Button>
                  )}
                </>
              )}
            </div>
          </section>

          <div class="mb-4">
            <input
              type="search"
              value={search}
              onInput={(e) => setSearch(e.currentTarget.value)}
              placeholder="Search inks or registry names…"
              class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary placeholder:text-text-muted focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
          </div>

          {filteredInks.length === 0 && filteredRegistry.length === 0 && (
            <div class="surface-card p-6 text-center">
              <p class="m-0 font-serif text-lg font-medium text-text-primary">
                {inks.length === 0 && registryNames.length === 0 ? 'No inks yet.' : 'No matches.'}
              </p>
              <p class="m-0 mt-1 text-sm text-text-secondary">
                {inks.length === 0
                  ? 'Inks appear here when you save them via the Converter or "+ Add ink".'
                  : 'Try a different search.'}
              </p>
            </div>
          )}

          {filteredInks.length > 0 && (
            <div class="surface-card mb-4 overflow-hidden">
              <p class="label border-b border-border-subtle px-4 py-2">
                Saved · {filteredInks.length}
              </p>
              {filteredInks.map((ink) => (
                <InkRow key={ink.id} ink={ink} bookId={book.id} />
              ))}
            </div>
          )}

          {filteredRegistry.length > 0 && (
            <div class="surface-card overflow-hidden">
              <p class="label border-b border-border-subtle px-4 py-2">
                Registry · showing {filteredRegistry.length}{!search && registryNames.length > 100 ? ` of ${registryNames.length}` : ''}
                <span class="ml-2 font-medium normal-case text-text-muted">click to claim</span>
              </p>
              {filteredRegistry.map((name) => (
                <RegistryRow key={name} name={name} onClaim={handleClaim} />
              ))}
              {!search && registryNames.length > 100 && (
                <p class="px-4 py-2 text-[11px] text-text-muted">Search to see more.</p>
              )}
            </div>
          )}

          <NewInkModal
            open={newOpen}
            onClose={() => setNewOpen(false)}
            onCreated={() => { setNewOpen(false); refetch(); }}
            bookId={book.id}
            bookName={book.name}
            prefilledName={prefilledName}
          />
        </>
      )}
    </div>
  );
};
